import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config.js';

/*
 * The single HTTP layer for the whole app.
 *
 * Responsibilities:
 *   - prefix every path with the configured API host
 *   - attach the Bearer token when the caller asks for it
 *   - turn non-2xx responses into a typed ApiError
 *   - notice an expired/invalid session and tell the app to sign out
 *
 * Nothing else in the codebase should call fetch() directly.
 */

/** Thrown for any non-2xx response, plus network failures. */
export class ApiError extends Error {
  constructor(message, { status = 0, detail = null, isNetwork = false } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.isNetwork = isNetwork;
  }
}

/* ------------------------------------------------------------------ *
 * Token storage
 *
 * The backend issues a 30-day Bearer JWT, so it has to outlive the tab —
 * that rules out sessionStorage and in-memory storage. An httpOnly cookie
 * would be safer against XSS but the API authenticates via the
 * Authorization header, so it isn't an option without backend changes.
 *
 * localStorage it is. The mitigation is keeping XSS impossible: no
 * dangerouslySetInnerHTML anywhere, and no injecting server strings as markup.
 * ------------------------------------------------------------------ */

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    // Private browsing modes can throw on storage access.
    return null;
  }
}

export function setToken(token) {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    /* nothing we can do; the session just won't persist */
  }
}

export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/* ------------------------------------------------------------------ *
 * Session expiry
 *
 * AuthProvider registers a handler here at mount. When any authenticated
 * request comes back 401, we clear the token and let the app redirect to
 * login — rather than leaving the user staring at a screen that silently
 * failed to load.
 * ------------------------------------------------------------------ */

let onUnauthorized = null;

export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

/**
 * Make a request.
 *
 * @param {string} method  HTTP verb
 * @param {string} path    path starting with '/', e.g. '/api/web/auth/me'
 * @param {object} [opts]
 * @param {object} [opts.body]  JSON body
 * @param {boolean} [opts.auth] send the Bearer token (default false)
 * @param {AbortSignal} [opts.signal]
 */
export async function request(method, path, { body, auth = false, signal } = {}) {
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (auth) {
    const token = getToken();
    if (!token) {
      onUnauthorized?.();
      throw new ApiError('Not signed in', { status: 401 });
    }
    headers.Authorization = `Bearer ${token}`;
  }

  let response;
  try {
    response = await fetch(API_BASE_URL + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError('Could not reach the server. Check your connection.', {
      isNetwork: true,
    });
  }

  // 204 and friends have no body to parse.
  const data = response.status === 204 ? {} : await response.json().catch(() => ({}));

  if (!response.ok) {
    if (response.status === 401 && auth) {
      clearToken();
      onUnauthorized?.();
    }
    throw new ApiError(errorMessage(data, response.status), {
      status: response.status,
      detail: data.detail ?? null,
    });
  }

  return data;
}

/** FastAPI puts human-readable text in `detail`; fall back to something useful. */
function errorMessage(data, status) {
  const { detail } = data;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  // Pydantic validation errors arrive as an array of objects.
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (status === 429) return 'Too many attempts. Please wait a little and try again.';
  if (status >= 500) return 'The server had a problem. Please try again shortly.';
  return 'Something went wrong. Please try again.';
}

export const api = {
  get: (path, opts) => request('GET', path, opts),
  post: (path, opts) => request('POST', path, opts),
};
